import { useState, useEffect } from 'react'
import { api, fixImageUrl, formatDateCourte } from '../api'
import { useCachedFetch } from '../lib/swr'
import { useModuleLabel } from '../lib/useModuleLabel'
import RinaContextCard from '../components/RinaContextCard'
import PastilleCompte from '../components/PastilleCompte'
import { useLocation, useSearch } from 'wouter'
import { ROUTES } from '../routes'
import { useActionsPortail } from '../contexts/navigation'
import { MVP } from '../mvp'
import FondPage from '../components/FondPage'
import EnteteModule from '../components/EnteteModule'
import OngletsHaut from '../components/OngletsHaut'
import BandeauEvenement from '../components/marche/BandeauEvenement'
import { useTheme } from '../theme'
import { usePanier } from '../contexts/panier'

// Module Evenements — facette CLIENT (ecran PLEIN fidele au prototype v4) :
// Produits / Stands / Infos (+ fiche produit), par evenement. Chrome : header
// « ← Lyannaj | <titre> | pastille lentille », carte RINA, bottom-nav dediee.
// La facette PRODUCTEUR vit dans MarcheModuleProducteur.jsx (S46).
// ADR-062 — titre du header : on est DANS un evenement donne -> son TYPE
// (EventTypeConfig, « ⛺ Marché », « 🎪 Village »...), pas le nom du module.
// AUCUN callback de navigation en prop : les actions viennent des contextes.

const ORANGE = 'rgb(var(--c-primary))'
const TEAL = 'rgb(var(--c-teal))'


// 'infos' n'est plus un onglet (S65) : le contexte vit dans le bandeau. Un
// ancien lien ?tab=infos retombe donc simplement sur « Produits ».
const ONGLETS_VALIDES = ['produits', 'stands']

const formatPrix = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`

function SectionLabel({ children, color = ORANGE }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] mb-2.5 mx-0.5" style={{ color }}>
      {children}
    </p>
  )
}

function EmptyTab({ tc, ts, icon, label }) {
  return (
    <div className="text-center pt-10 animate-fade-up">
      <span className="text-4xl block mb-2">{icon}</span>
      <p className={`text-sm font-semibold ${tc}`}>{label}</p>
      <p className={`text-xs mt-1 ${ts}`}>Reviens bientôt pour découvrir le prochain marché</p>
    </div>
  )
}

export default function MarcheModule({ marcheId }) {
  const { productFavs } = usePanier()
  const { fermerModule, handleRinaClick, openMarche } = useActionsPortail()
  const onExit = () => fermerModule()
  // Fiche producteur : une VRAIE adresse (/p/:id, S62) — avant, une modale qui
  // ne changeait pas l'URL (retour Daniel : impossible de partager un producteur
  // qu'on vient de decouvrir). Le contexte de l'evenement passe en query :
  // `prix` (le BO decide si les prix s'affichent), `stand` (emplacement du jour).
  const [, setLocation] = useLocation()
  const ouvrirProducteur = (orgId, afficherPrix, emplacement) => {
    const q = new URLSearchParams()
    if (afficherPrix !== false) q.set('prix', '1')
    if (emplacement) q.set('stand', emplacement)
    setLocation(ROUTES.producteurPublic.vers(orgId) + (q.size ? `?${q}` : ''))
    window.scrollTo(0, 0)
  }
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const glass = 'panneau-verre'

  // Nom du lieu (ADR-062) — pilote par le backend, servi depuis le cache SWR.
  const module = useModuleLabel('MARCHE')

  const [marche, setMarche] = useState(null)
  const [stands, setStands] = useState([])
  const [loading, setLoading] = useState(true)

  // Onglet + fiche produit PORTES PAR L'URL (?tab=&p=) — ADR-065 : l'URL est la
  // source de verite. Avant, deux useState : le refresh et le partage d'un lien
  // perdaient l'onglet, et le « retour » navigateur sortait du marche au lieu de
  // refermer la fiche produit (retour Daniel).
  const params = new URLSearchParams(useSearch())
  const tabDemande = params.get('tab')
  const tab = ONGLETS_VALIDES.includes(tabDemande) ? tabDemande : 'produits'
  const produitDemande = params.get('p')
  // `replace` sur l'onglet (une page = un marche, pas trois entrees d'historique)
  // et `push` sur la fiche produit (le back doit la refermer, pas quitter).
  const majUrl = (maj, { replace = false } = {}) => {
    const q = new URLSearchParams(params)
    Object.entries(maj).forEach(([k, v]) => (v == null ? q.delete(k) : q.set(k, v)))
    setLocation(ROUTES.marche.vers(marcheId) + (q.size ? `?${q}` : ''), { replace })
    window.scrollTo(0, 0)
  }
  const setTab = (k) => majUrl({ tab: k === 'produits' ? null : k, p: null }, { replace: true })
  const ouvrirProduit = (id) => majUrl({ p: id })
  const fermerProduit = () => majUrl({ p: null })
  // Cache SWR (S28) : reouvrir un marche deja consulte l'affiche INSTANTANEMENT
  // depuis la derniere valeur connue, puis rafraichit en silence. Sans cela,
  // chaque retour repassait par le squelette de chargement.
  const { data: detailCache, loading: chargementDetail } = useCachedFetch(
    marcheId ? `marche/${marcheId}` : null,
    () => api.getMarcheDetail(marcheId),
    { enabled: !!marcheId },
  )

  useEffect(() => {
    if (!marcheId) { setLoading(false); return }
    setLoading(chargementDetail && !detailCache)
    if (detailCache) {
      setMarche(detailCache)
      setStands(detailCache?.stands || [])
    }
  }, [marcheId, detailCache, chargementDetail])

  // Agrege les offres des stands par produit (quels stands le proposent, prix mini).
  const productMap = new Map()
  stands.forEach((s) => {
    (s.offers || []).forEach((o) => {
      const p = o.product
      if (!p) return
      const key = p.id || p.nom
      const prix = o.prix != null ? Number(o.prix) : null
      const entry = productMap.get(key) || {
        id: key,
        nom: p.nom || 'Produit',
        emoji: p.category?.emoji || '🥭',
        categorie: p.category?.nom || 'Autres',
        prixMin: null,
        stands: [],
      }
      if (prix != null && (entry.prixMin == null || prix < entry.prixMin)) entry.prixMin = prix
      entry.stands.push({ offerId: o.id, stand: s, prix })
      productMap.set(key, entry)
    })
  })
  const products = [...productMap.values()]

  const byCategory = {}
  products.forEach((p) => {
    if (!byCategory[p.categorie]) byCategory[p.categorie] = []
    byCategory[p.categorie].push(p)
  })
  const categories = Object.keys(byCategory).sort((a, b) => a.localeCompare(b, 'fr'))
  categories.forEach((c) => byCategory[c].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')))

  // Le produit de l'URL, resolu contre les produits charges : tant que le detail
  // n'est pas arrive (deep-link + refresh), `p` ne designe encore rien.
  // La fiche produit n'existe que dans l'onglet Produits : un `p` porte par une
  // autre URL (onglet different, id inconnu) est simplement ignore.
  const selectedProduct =
    produitDemande && tab === 'produits'
      ? products.find((x) => String(x.id) === produitDemande) || null
      : null

  const tabs = [
    { k: 'produits', e: '🥭', l: 'Produits' },
    { k: 'stands', e: '🧑🏾‍🌾', l: 'Stands' },
  ]

  const rinaMsg = selectedProduct
    ? 'Ce produit et où le trouver au marché. Je vous oriente vers le bon stand.'
    : tab === 'stands' ? 'Tous les producteurs présents — je peux vous guider vers un stand.'
    : 'Les produits attendus au marché. Touchez-en un pour voir qui le vend.'

  return (
    <FondPage halos>

      <div className="pb-10 max-w-md mx-auto relative z-[2]">
        {/* Header module */}
        {/* On est DANS un evenement donne : c'est son IDENTITE qui titre (S65,
            ADR-069). Le TYPE reste affiche — il descend en sous-titre avec la
            date, ce que l'ADR-062 demande (le type, jamais le nom du module).
            Avant, l'en-tete disait « ⛺ Marché » : sur /marche/:id, rien a
            l'ecran ne disait SUR QUEL marche on etait (retour Daniel). */}
        <EnteteModule
          retour={{ label: 'Lyannaj', onClick: onExit }}
          titre={marche?.nom || marche?.lieu || `${module.emoji} ${module.nom}`}
          sousTitre={
            marche
              ? `${marche.typeConfig?.icone || module.emoji} ${marche.typeConfig?.nom || module.nom}${marche.date ? ` · ${formatDateCourte(marche.date)}` : ''}`
              : null
          }
        >
          <PastilleCompte />
        </EnteteModule>

        <div className="px-4 pt-1">
          {/* Contexte de l'evenement — sur TOUS les onglets (S65). Masque en fiche
              produit : celle-ci est un ecran a part, avec son propre retour. */}
          {!loading && !selectedProduct && (
            <>
              <BandeauEvenement marche={marche} nbStands={stands.length} nbProduits={products.length} />
              {/* Onglets EN HAUT (S65) : deux segments coiffes du contexte — une
                  subdivision du contenu, pas un changement de lieu (la barre
                  basse d'un module se confondait avec celle du hub). */}
              <OngletsHaut onglets={tabs} actif={tab} onSelect={setTab} />
            </>
          )}

          {/* RINA DANS l'onglet (S65) : sous les onglets, elle commente le
              contenu affiche — au-dessus, elle commentait la page entiere.
              `cle` par onglet : son mot n'est pas le meme sur Produits et sur
              Stands, sa fermeture se memorise donc separement. */}
          {!loading && (
            <RinaContextCard
              onClick={handleRinaClick}
              message={rinaMsg}
              cle={selectedProduct ? 'marche-produit' : `marche-${tab}`}
            />
          )}

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className={`h-14 rounded-xl animate-pulse bg-ink/5`} />
              ))}
            </div>
          ) : selectedProduct ? (
            /* ----- FICHE PRODUIT : « Ou en trouver » ----- */
            <>
              <button
                onClick={fermerProduit}
                className={`flex items-center gap-1.5 text-sm font-semibold ${ts} hover:text-primary transition mb-3`}
              >
                ‹ Produits
              </button>

              <div className={`rounded-[26px] p-6 text-center mb-4 animate-fade-up ${glass}`}>
                <div className="text-6xl">{selectedProduct.emoji}</div>
                <p className={`font-serif text-[26px] mt-1.5 ${tc}`}>{selectedProduct.nom}</p>
                <p className={`text-xs mt-1 ${ts}`}>
                  {selectedProduct.stands.length} stand{selectedProduct.stands.length > 1 ? 's' : ''} en proposent
                  {selectedProduct.prixMin != null && ` · dès ${formatPrix(selectedProduct.prixMin)}`}
                </p>
              </div>

              <SectionLabel color={TEAL}>Où en trouver</SectionLabel>
              <div className="flex flex-col gap-2.5">
                {selectedProduct.stands.map(({ offerId, stand, prix }, i) => {
                  const org = stand.organization
                  const isFav = productFavs?.isSaved(offerId)
                  return (
                    <div
                      key={offerId || i}
                      role="button"
                      tabIndex={0}
                      onClick={() => org?.id && ouvrirProducteur(org.id, stand.afficherPrix, stand.emplacement)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && org?.id && ouvrirProducteur(org.id, stand.afficherPrix, stand.emplacement)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-2xl animate-fade-up cursor-pointer active:scale-[0.98] transition-transform ${glass}`}
                      style={{ animationDelay: `${i * 0.04}s` }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-base flex-shrink-0 overflow-hidden border-2 border-white/80"
                        style={{ background: org?.logo ? 'transparent' : 'linear-gradient(135deg, rgb(var(--c-teal)), #0d6b5f)' }}
                      >
                        {org?.logo ? <img src={fixImageUrl(org.logo)} alt="" className="w-full h-full object-cover" /> : '🧑🏾‍🌾'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${tc}`}>{org?.nomCommercial || org?.nom}</p>
                        <p className={`text-xs ${ts}`}>
                          {stand.emplacement ? `Stand ${stand.emplacement}` : 'Stand'}
                          {prix != null ? ` · ${formatPrix(prix)}` : ''}
                        </p>
                      </div>
                      {offerId && productFavs && MVP.FAVORIS && (
                        <button
                          onClick={(e) => { e.stopPropagation(); productFavs.toggle(offerId) }}
                          className={`text-lg flex-shrink-0 ${isFav ? 'text-primary' : ts}`}
                          aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        >
                          {isFav ? '★' : '☆'}
                        </button>
                      )}
                      <span className={`text-sm ${ts}`}>›</span>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <>
              {/* ===== PRODUITS ===== */}
              {tab === 'produits' && (
                products.length === 0 ? (
                  <EmptyTab tc={tc} ts={ts} icon="🥭" label="Aucun produit annoncé pour cet évènement" />
                ) : (
                  <>
                    {categories.map((cat) => (
                      <div key={cat} className="mb-5">
                        <SectionLabel>{cat}</SectionLabel>
                        <div className="grid grid-cols-2 gap-2.5">
                          {byCategory[cat].map((p, i) => (
                            <button
                              key={p.id}
                              onClick={() => ouvrirProduit(p.id)}
                              className={`flex items-center gap-3 p-3.5 rounded-[20px] text-left animate-fade-up active:scale-[0.97] transition-transform ${glass}`}
                              style={{ animationDelay: `${i * 0.03}s` }}
                            >
                              <span
                                className="w-11 h-11 rounded-[14px] flex items-center justify-center text-2xl flex-shrink-0"
                                style={{ background: 'linear-gradient(135deg, rgba(244,137,83,0.25), rgba(20,184,166,0.2))' }}
                              >
                                {p.emoji}
                              </span>
                              <div className="min-w-0">
                                <p className={`text-sm font-bold truncate ${tc}`}>{p.nom}</p>
                                <p className={`text-[11px] ${ts}`}>
                                  {p.stands.length} stand{p.stands.length > 1 ? 's' : ''}
                                  {p.prixMin != null && ` · ${formatPrix(p.prixMin)}`}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )
              )}

              {/* ===== STANDS ===== */}
              {tab === 'stands' && (
                stands.length === 0 ? (
                  <EmptyTab tc={tc} ts={ts} icon="🧑🏾‍🌾" label="Aucun stand inscrit pour ce marché" />
                ) : (
                  <>
                    <SectionLabel color={TEAL}>Stands présents ({stands.length})</SectionLabel>
                    <div className="flex flex-col gap-2.5">
                      {stands.map((s, i) => {
                        const org = s.organization
                        const nb = s.offers?.length || 0
                        return (
                          <div
                            key={org?.id || i}
                            role="button"
                            tabIndex={0}
                            onClick={() => org?.id && ouvrirProducteur(org.id, s.afficherPrix, s.emplacement)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && org?.id && ouvrirProducteur(org.id, s.afficherPrix, s.emplacement)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-2xl animate-fade-up cursor-pointer active:scale-[0.98] transition-transform ${glass}`}
                            style={{ animationDelay: `${i * 0.04}s` }}
                          >
                            <div
                              className="w-11 h-11 rounded-full flex items-center justify-center text-lg flex-shrink-0 overflow-hidden border-2 border-white/80"
                              style={{ background: org?.logo ? 'transparent' : 'linear-gradient(135deg, rgb(var(--c-teal)), #0d6b5f)' }}
                            >
                              {org?.logo ? <img src={fixImageUrl(org.logo)} alt="" className="w-full h-full object-cover" /> : '🧑🏾‍🌾'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold truncate ${tc}`}>{org?.nomCommercial || org?.nom}</p>
                              <p className={`text-xs ${ts}`}>
                                {s.emplacement ? `Stand ${s.emplacement}` : 'Stand'}
                                {nb > 0 ? ` · ${nb} produit${nb > 1 ? 's' : ''}` : ''}
                              </p>
                            </div>
                            <span className={`text-sm ${ts}`}>›</span>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )
              )}
            </>
          )}
        </div>
      </div>

    </FondPage>
  )
}
